import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  EventDiscoveryClient,
  EventDiscoveryQuotaConfig,
} from 'src/WebApiController';

export const useEventDiscoveryConfig = () => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError, showErrorDialog } = useClientLogContext();
  const queryClient = useQueryClient();

  const shouldQuery = Boolean(activeAccountWebClientConfig.activeAccountId);

  const eventDiscoveryConfigSettingsQueryKey = [
    'EventDiscoveryClient.getEventDiscoveryConfigSettings',
    activeAccountWebClientConfig.activeAccountId,
  ];
  const eventDiscoveryConfigSettingsQuery = useQuery({
    queryKey: eventDiscoveryConfigSettingsQueryKey,
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }

      const config = await new EventDiscoveryClient(
        activeAccountWebClientConfig
      ).getEventDiscoveryConfigSettings();

      return config;
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: true,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('EventDiscoveryClient.getEventDiscoveryConfig', error);
      },
    },
  });

  const updateEventDiscoveryConfigMutation = useMutation({
    mutationFn: async ({ input }: { input: EventDiscoveryQuotaConfig }) => {
      return await new EventDiscoveryClient(
        activeAccountWebClientConfig
      ).unlockEventDemandData(input);
    },
    onError: (err: ErrorTypes, { input }) => {
      showErrorDialog('EventDiscoveryClient.UnlockEventDemandData', err, {
        trackErrorData: { input },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: eventDiscoveryConfigSettingsQueryKey,
      });
    },
  });

  return {
    eventDiscoveryConfigSettingsQuery: eventDiscoveryConfigSettingsQuery.data,
    eventDiscoveryConfigSettingsQueryIsLoading:
      eventDiscoveryConfigSettingsQuery.isLoading,
    updateEventDiscoveryConfigMutation:
      updateEventDiscoveryConfigMutation.mutateAsync,
  };
};
